<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import {ref, computed} from "vue";

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const hoverMainContentLinkContainer = ref(false);

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const displayInColumns = computed(() => props.resource.display_content ?? false);
const maxCols = computed(() => props.resource.columns_count || 2);
const itemCount = computed(() => props.resource.contents?.length || 0);
const gridColsClass = computed(() => {
    if (!displayInColumns.value) return '';
    const cols = Math.min(maxCols.value, itemCount.value);
    if (cols >= 3) return 'lg:grid-cols-3';
    if (cols >= 2) return 'lg:grid-cols-2';
    return '';
});

const imageSizeClass = computed(() => {
    // Backwards compatibility: check for old retina resize setting
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    // New size setting
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16'; // medium
    }
});
</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainContentLinkContainer = true"
             @mouseleave="hoverMainContentLinkContainer = false"
             :id="resource.unique_id"
        >
            <editable-resource-image-field
                resource-image-path="main_image"
                :resource-content="resource"
                :img-class="imageSizeClass"
                img-name="main_image"
                :show-add-image-button="hoverMainContentLinkContainer"
            />

            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <div :class="[
            (resource.title || resource.description) ? 'mt-4 md:mt-6' : '',
            displayInColumns ? 'grid gap-4' : 'flex flex-col gap-2',
            gridColsClass
        ]">
            <a
                v-for="(content, index) in resource.contents"
                :key="index"
                :href="content.url"
                target="_blank"
                rel="noopener noreferrer"
                :class="[
                    'group flex items-start gap-3',
                    displayInColumns ? 'p-4 border rounded' : 'py-2',
                    portalContentAndDesignStore.getPageContentTextColorClass
                ]"
            >
                <svg class="w-4 h-4 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                <div class="flex flex-col">
                    <span class="group-hover:underline">{{ content.title }}</span>
                    <span v-if="content.description" class="text-sm opacity-60">{{ content.description }}</span>
                </div>
            </a>
        </div>
    </div>
</template>
